import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Markdown } from "@/components/shared";
import { Panel, inputCls } from "@/components/cms/kit";
import {
  Sparkles, Type, AlignLeft, Search, FileText, Tags, HelpCircle,
  RefreshCw, Wand2, Loader2, Check, X, PenLine, Undo2,
} from "lucide-react";

/* Logical Task Router actions → editor field. Structured tasks parse JSON. */
const ACTIONS = [
  { task: "article.generate_headline", label: "Headline", icon: Type, target: "Title", ctx: "meta" },
  { task: "article.generate_summary", label: "Summary", icon: AlignLeft, target: "Excerpt / Subtitle", ctx: "summary" },
  { task: "article.generate_seo_title", label: "SEO Title", icon: Search, target: "SEO Title", ctx: "seo" },
  { task: "article.generate_meta_description", label: "Meta Description", icon: PenLine, target: "Meta Description", ctx: "seo" },
  { task: "article.generate_tags", label: "Tags", icon: Tags, target: "Tags", ctx: "seo", structured: true },
  { task: "article.generate_faqs", label: "FAQs", icon: HelpCircle, target: "FAQs", ctx: "seo", structured: true },
  { task: "article.generate_article", label: "Full Article", icon: FileText, target: "whole article", ctx: "brief", structured: true, full: true },
  { task: "article.rewrite", label: "Rewrite Body", icon: RefreshCw, target: "Body", ctx: "body" },
  { task: "article.improve", label: "Improve Body", icon: Wand2, target: "Body", ctx: "body" },
];

const TONES = [
  { value: "news", label: "News", instr: "Tone: neutral, factual news style." },
  { value: "professional", label: "Professional", instr: "Tone: professional and authoritative." },
  { value: "casual", label: "Casual", instr: "Tone: casual and conversational." },
  { value: "expert", label: "Expert", instr: "Tone: expert, technical and precise." },
  { value: "explainer", label: "Explainer", instr: "Tone: friendly explainer that teaches concepts simply." },
];
const LENGTHS = [
  { value: "short", label: "Short", instr: "Length: concise and to the point." },
  { value: "standard", label: "Standard", instr: "Length: standard editorial depth." },
  { value: "detailed", label: "Detailed", instr: "Length: detailed and comprehensive coverage." },
];

const shortKey = (t) => t.replace("article.", "");

const toTags = (out) => {
  const arr = Array.isArray(out) ? out : out && Array.isArray(out.tags) ? out.tags : [];
  return arr.map((s) => String(s).trim()).filter(Boolean);
};
const toFaqs = (out) => {
  const arr = Array.isArray(out) ? out : out && Array.isArray(out.faqs) ? out.faqs : [];
  return arr
    .map((x) => ({ q: String(x?.question ?? x?.q ?? "").trim(), a: String(x?.answer ?? x?.a ?? "").trim() }))
    .filter((x) => x.q || x.a);
};

/* Normalize model Markdown to the exact subset the KLYROO renderer supports
   (components/shared.jsx renderBlocks): '-' bullets only (not '*'/'+'), and a
   blank line between block-type changes so lists/headings render correctly. */
export function normalizeMarkdown(md) {
  let lines = String(md || "").replace(/\r\n/g, "\n").split("\n")
    .map((l) => l.replace(/^(\s*)[*+]\s+/, "$1- "));
  const kind = (l) => {
    const t = l.trim();
    if (!t) return "blank";
    if (/^#{1,6}\s+/.test(t)) return "heading";
    if (/^-\s+/.test(t)) return "ul";
    if (/^\d+\.\s+/.test(t)) return "ol";
    if (/^>\s?/.test(t)) return "quote";
    return "p";
  };
  const out = [];
  let prev = "blank";
  for (const l of lines) {
    const cur = kind(l);
    if (cur !== "blank" && prev !== "blank" && cur !== prev) out.push("");
    out.push(l);
    prev = cur;
  }
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

/* Build the minimal relevant context per action from current form state. */
function buildContext(action, f) {
  const body = (f.body_sections || [])
    .map((s) => (s.heading ? `## ${s.heading}\n` : "") + (s.content || ""))
    .join("\n\n")
    .trim();
  const join = (parts) => parts.filter((p) => p && String(p).trim()).join("\n\n").trim();
  switch (action.ctx) {
    case "meta":
      return join([f.title && `Working title: ${f.title}`, f.category && `Category: ${f.category}`, body && `Content:\n${body}`]);
    case "summary":
      return join([f.title && `Headline: ${f.title}`, body && `Content:\n${body}`]);
    case "seo":
      return join([f.title && `Headline: ${f.title}`, f.subtitle && `Summary: ${f.subtitle}`, body && `Content:\n${body}`]);
    case "brief":
      return join([
        f.title && `Title / topic: ${f.title}`,
        f.category && `Category: ${f.category}`,
        f.content_type && `Type: ${f.content_type}`,
        f.subtitle && `Angle / excerpt: ${f.subtitle}`,
        (f.key_takeaways || []).length && `Points to cover: ${(f.key_takeaways || []).join("; ")}`,
      ]);
    case "body":
    default:
      return body;
  }
}

export function AiAssistPanel({ f, set }) {
  const [providers, setProviders] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [pid, setPid] = useState("");
  const [model, setModel] = useState("");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("standard");
  const [busyTask, setBusyTask] = useState(null);
  const [modal, setModal] = useState(null);
  const [undo, setUndo] = useState(null); // { label, fields: [{key, prev}] }
  const reqRef = useRef(0); // guards against stale responses (closed/superseded requests)

  useEffect(() => {
    api.llmProviders().then((d) => {
      const list = d.providers || [];
      setProviders(list);
      const preferred = list.find((p) => p.configured && p.enabled) || list[0];
      if (preferred) setPid(preferred.id);
    }).catch(() => setProviders([]));
    api.llmCatalog().then((d) => setCatalog(d.catalog || {})).catch(() => setCatalog({}));
  }, []);

  const models = useMemo(() => (catalog[pid] || []).filter((m) => m.status !== "deprecated"), [catalog, pid]);
  useEffect(() => {
    const rec = models.find((m) => m.recommended) || models[0];
    setModel(rec ? rec.id : "");
  }, [pid, catalog]); // eslint-disable-line

  const activeProvider = providers.find((p) => p.id === pid);
  const ready = !!(activeProvider && activeProvider.configured && activeProvider.enabled && model);

  const styleInstruction = () => {
    const t = TONES.find((x) => x.value === tone)?.instr || "";
    const l = LENGTHS.find((x) => x.value === length)?.instr || "";
    return `${t} ${l}`.trim();
  };

  const runAction = async (action) => {
    const input = buildContext(action, f);
    if (!input) {
      toast.error(action.ctx === "body" ? "Add some body content first" : "Add a title or some content first");
      return;
    }
    setBusyTask(action.task);
    setModal({ action, loading: true });
    const myId = ++reqRef.current;
    try {
      const r = await api.llmRouteTask({ provider: pid, model, task: action.task, input, style: styleInstruction() });
      if (reqRef.current !== myId) return; // request was closed or superseded — ignore stale result
      if (r.success) setModal({ action, result: r.output, usage: r.usage });
      else setModal({ action, error: r.error || { code: "PROVIDER_API_ERROR", message: "Failed" } });
    } catch (e) {
      if (reqRef.current !== myId) return;
      setModal({ action, error: { code: "REQUEST_FAILED", message: e.response?.data?.detail || "Request failed" } });
    } finally {
      if (reqRef.current === myId) setBusyTask(null);
    }
  };

  const closeModal = () => { reqRef.current++; setBusyTask(null); setModal(null); };

  // Snapshot previous values, apply new ones, and remember for Undo.
  const applyChanges = (label, changes) => {
    setUndo({ label, fields: changes.map((c) => ({ key: c.key, prev: f[c.key] })) });
    changes.forEach((c) => set(c.key, c.value));
  };

  const applyResult = () => {
    const { action, result } = modal;
    let changes = [];
    switch (action.task) {
      case "article.generate_headline": changes = [{ key: "title", value: String(result).trim() }]; break;
      case "article.generate_summary": changes = [{ key: "subtitle", value: String(result).trim() }]; break;
      case "article.generate_seo_title": changes = [{ key: "seo_title", value: String(result).trim() }]; break;
      case "article.generate_meta_description": changes = [{ key: "meta_description", value: String(result).trim() }]; break;
      case "article.generate_tags": changes = [{ key: "tags", value: toTags(result) }]; break;
      case "article.generate_faqs": changes = [{ key: "faqs", value: toFaqs(result) }]; break;
      case "article.generate_article": changes = fullArticleChanges(result); break;
      case "article.rewrite":
      case "article.improve":
        changes = [{ key: "body_sections", value: [{ heading: "", content: normalizeMarkdown(String(result)) }] }]; break;
      default: break;
    }
    applyChanges(action.label, changes);
    toast.success(`Applied to ${action.target}`);
    setModal(null);
  };

  const undoApply = () => {
    if (!undo) return;
    undo.fields.forEach((u) => set(u.key, u.prev));
    toast.success(`Reverted: ${undo.label}`);
    setUndo(null);
  };

  return (
    <Panel title="AI Assist" testid="ai-assist-panel">
      <div className="flex items-start gap-2 -mt-1">
        <Sparkles size={15} className="text-klyroo-cyan mt-0.5 shrink-0" />
        <p className="text-xs text-slate-500">Generate a draft with your configured LLM, preview it, then choose to apply. Nothing runs automatically — only when you click an action, and nothing overwrites a field until you press Apply.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <SelField label="Provider" testid="ai-provider-select" value={pid} onChange={setPid}>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>{p.name}{p.configured ? (p.enabled ? "" : " (disabled)") : " (not configured)"}</option>
          ))}
          {providers.length === 0 && <option value="">No providers</option>}
        </SelField>
        <SelField label="Model" testid="ai-model-select" value={model} onChange={setModel}>
          {models.map((m) => <option key={m.id} value={m.id}>{m.display_name}{m.recommended ? " ★" : ""}</option>)}
          {models.length === 0 && <option value="">No available models</option>}
        </SelField>
        <SelField label="Tone" testid="ai-tone-select" value={tone} onChange={setTone}>
          {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </SelField>
        <SelField label="Length" testid="ai-length-select" value={length} onChange={setLength}>
          {LENGTHS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </SelField>
      </div>

      {!ready && (
        <p className="text-xs text-amber-300" data-testid="ai-not-ready">
          Selected provider isn't ready. Enable & configure it in the AI / LLM tab to use AI Assist.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {ACTIONS.map((a) => {
          const Icon = a.icon;
          const loading = busyTask === a.task;
          return (
            <button key={a.task} type="button" onClick={() => runAction(a)} disabled={!ready || !!busyTask}
              data-testid={`ai-action-${shortKey(a.task)}`}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-slate-200 text-sm hover:bg-white/10 hover:border-klyroo-cyan/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? <Loader2 size={14} className="animate-spin text-klyroo-cyan" /> : <Icon size={14} className="text-klyroo-cyan" />}
              <span className="truncate">{a.label}</span>
            </button>
          );
        })}
      </div>

      {undo && (
        <button type="button" onClick={undoApply} data-testid="ai-undo"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 text-xs hover:bg-white/10">
          <Undo2 size={13} /> Undo last AI apply ({undo.label})
        </button>
      )}

      {modal && <PreviewModal state={modal} onApply={applyResult} onRegenerate={() => runAction(modal.action)} onClose={closeModal} />}
    </Panel>
  );

  function fullArticleChanges(result) {
    const r = result || {};
    const ch = [];
    if (r.title) ch.push({ key: "title", value: String(r.title).trim() });
    if (r.excerpt) ch.push({ key: "subtitle", value: String(r.excerpt).trim() });
    if (Array.isArray(r.key_takeaways)) ch.push({ key: "key_takeaways", value: r.key_takeaways.map((s) => String(s).trim()).filter(Boolean) });
    if (Array.isArray(r.body_sections)) ch.push({
      key: "body_sections",
      value: r.body_sections.map((s) => ({ heading: String(s?.heading || "").trim(), content: normalizeMarkdown(s?.content || "") })),
    });
    if (r.faqs) ch.push({ key: "faqs", value: toFaqs(r.faqs) });
    if (r.seo_title) ch.push({ key: "seo_title", value: String(r.seo_title).trim() });
    if (r.meta_description) ch.push({ key: "meta_description", value: String(r.meta_description).trim() });
    if (r.tags) ch.push({ key: "tags", value: toTags(r.tags) });
    return ch;
  }
}

function SelField({ label, testid, value, onChange, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-1.5">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>{children}</select>
    </label>
  );
}

function PreviewModal({ state, onApply, onRegenerate, onClose }) {
  const { action, loading, result, error, usage } = state;
  return (
    <div className="fixed inset-0 z-[130] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl card-surface rounded-2xl p-5 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ai-preview-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-klyroo-cyan" />
            <span className="font-display font-bold text-white">{action.label}</span>
            <span className="text-xs text-slate-500">→ {action.target}</span>
          </div>
          <button type="button" onClick={onClose} data-testid="ai-modal-close" className="p-1 text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-auto rounded-xl border border-white/10 bg-klyroo-bg/60 p-4 min-h-[120px]" data-testid="ai-preview-output">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="ai-loading"><Loader2 size={16} className="animate-spin text-klyroo-cyan" /> Generating…</div>
          ) : error ? (
            <div className="text-sm" data-testid="ai-error">
              <span className="text-klyroo-red font-mono">✕ {error.code}</span>
              <span className="text-slate-400 ml-2">{error.message}</span>
            </div>
          ) : (
            <PreviewBody action={action} result={result} />
          )}
        </div>

        {!loading && !error && usage && (
          <div className="text-[11px] font-mono text-slate-500 mt-2">tokens: {usage.totalTokens ?? "?"}</div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} data-testid="ai-cancel" className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5">Cancel</button>
          <button type="button" onClick={onRegenerate} disabled={loading} data-testid="ai-regenerate" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 text-slate-200 text-sm hover:bg-white/10 disabled:opacity-50"><RefreshCw size={14} /> Regenerate</button>
          <button type="button" onClick={onApply} disabled={loading || !!error} data-testid="ai-apply" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm hover:bg-white disabled:opacity-50"><Check size={14} /> Apply</button>
        </div>
      </div>
    </div>
  );
}

function PreviewBody({ action, result }) {
  if (action.task === "article.generate_tags") {
    const tags = toTags(result);
    return (
      <div className="flex flex-wrap gap-2" data-testid="ai-preview-tags">
        {tags.length ? tags.map((t, i) => <span key={i} className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1 text-sm text-slate-200">{t}</span>)
          : <span className="text-slate-500 text-sm italic">No tags returned</span>}
      </div>
    );
  }
  if (action.task === "article.generate_faqs") {
    return <FaqPreview faqs={toFaqs(result)} />;
  }
  if (action.full) {
    const r = result || {};
    return (
      <div className="space-y-4 text-sm" data-testid="ai-preview-full">
        {r.title && <div><span className="block text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan mb-0.5">Title</span><div className="text-white font-display font-bold text-base">{r.title}</div></div>}
        {r.excerpt && <div><span className="block text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan mb-0.5">Excerpt</span><div className="text-slate-300">{r.excerpt}</div></div>}
        {Array.isArray(r.key_takeaways) && r.key_takeaways.length > 0 && (
          <div><span className="block text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan mb-0.5">Key Takeaways</span><ul className="list-disc pl-5 text-slate-300">{r.key_takeaways.map((k, i) => <li key={i}>{k}</li>)}</ul></div>
        )}
        {Array.isArray(r.body_sections) && (
          <div data-testid="ai-preview-sections"><span className="block text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan mb-0.5">Body ({r.body_sections.length} sections)</span>
            <div className="space-y-3 mt-1">
              {r.body_sections.map((s, i) => (
                <div key={i} className="rounded-lg border border-white/10 p-3">
                  <div className="text-white font-semibold mb-1">{s.heading}</div>
                  <div className="prose-invert text-slate-300"><Markdown content={normalizeMarkdown(s.content || "")} /></div>
                </div>
              ))}
            </div>
          </div>
        )}
        {Array.isArray(r.faqs) && r.faqs.length > 0 && (<div><span className="block text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan mb-0.5">FAQs</span><FaqPreview faqs={toFaqs(r.faqs)} /></div>)}
        <div className="grid sm:grid-cols-2 gap-3">
          {r.seo_title && <div><span className="block text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan mb-0.5">SEO Title</span><div className="text-slate-300">{r.seo_title}</div></div>}
          {r.meta_description && <div><span className="block text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan mb-0.5">Meta Description</span><div className="text-slate-300">{r.meta_description}</div></div>}
        </div>
        {Array.isArray(r.tags) && r.tags.length > 0 && (
          <div><span className="block text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan mb-0.5">Tags</span><div className="flex flex-wrap gap-2 mt-1">{toTags(r.tags).map((t, i) => <span key={i} className="bg-white/5 border border-white/10 rounded-lg px-2 py-0.5 text-xs text-slate-200">{t}</span>)}</div></div>
        )}
      </div>
    );
  }
  const isBody = ["article.rewrite", "article.improve"].includes(action.task);
  if (isBody) return <div className="prose-invert"><Markdown content={normalizeMarkdown(String(result || ""))} /></div>;
  return <p className="text-slate-200 text-sm whitespace-pre-wrap break-words">{String(result || "")}</p>;
}

function FaqPreview({ faqs }) {
  return (
    <div className="space-y-3" data-testid="ai-preview-faqs">
      {faqs.length ? faqs.map((q, i) => (
        <div key={i}>
          <div className="text-white font-semibold text-sm">{q.q}</div>
          <div className="text-slate-400 text-sm">{q.a}</div>
        </div>
      )) : <span className="text-slate-500 text-sm italic">No FAQs returned</span>}
    </div>
  );
}
